import React from 'react';
import { string } from 'prop-types';
import { Box, Text, Image, Heading, Center } from '@chakra-ui/react';

import { useGuest } from '@/context/guest';

import generateShift from '@/hooks/useShift';
import useInvitation from '@/hooks/useInvitation';
import useLang from '@/hooks/useLang';

import QRCard from '@/components/Common/QRCard/Lazy';
import WithAnimation from '@/components/Common/WithAnimation';
import ViewLocationButton from '@/components/Common/Buttons/ViewLocation';

import { ASSETS_CHEERS, ASSETS_CINCIN } from '@/constants/assets';
import { ENABLE_MUSLIM_LANGUAGE, ENABLE_QR_INVITATION } from '@/constants/feature-flags';
import {
  WEDDING_AKAD_TIME,
  WEDDING_AKAD_LOC_NAME,
  WEDDING_AKAD_LOC_ROAD,
  WEDDING_AKAD_DRESSCODE,
  WEDDING_AKAD_NOTE,
  WEDDING_RESEPSI_LOC_NAME,
  WEDDING_RESEPSI_LOC_ROAD,
  WEDDING_RESEPSI_DRESSCODE,
  WEDDING_RESEPSI_NOTE,
  GOOGLE_MAPS_RESEPSI,
  GOOGLE_MAPS_AKAD,
  WEDDING_RESEPSI_TIME,
} from '@/constants';
import { ID_QRCODE } from '@/constants/identifier';
import { BUTTON_PADDING_PROPS } from '@/constants/colors';
import { TEXT_PROPS } from '@/constants/default-props';

import txt from './locales';

/**
 * function to render WeddingSectionV3 component
 * @returns {JSX.Element}
 */
function WeddingSection({ ...rest }) {
  const { guest } = useGuest();

  const isInvitation = useInvitation();
  const shiftTime = generateShift(guest.shift);
  const lang = useLang();

  return (
    <Box {...rest}>
      {shiftTime != 'resepsi' &&
      <WithAnimation>
        <Box
          width="50px"
          height="3px"
          mx="auto"
          mb="16px"
          background="bgSecondary"
          borderRadius="8px"
          transform="rotate(-90deg)"
        />
        <WithAnimation>
          <Center p="24px 0 8px">
            <Image src={ASSETS_CINCIN} maxW="48px" />
          </Center>
        </WithAnimation>
        <Center flexDir="column" marginBottom={isInvitation ? '10px' : '60px'}>
          <Heading {...TEXT_PROPS.heading} textTransform="none">
            {ENABLE_MUSLIM_LANGUAGE ? txt.akad[lang] : txt.holy[lang]}
          </Heading>
          <Box {...TEXT_PROPS.body} mb={isInvitation ? '' : '36px'} textAlign="center">
            <Text fontSize="24px">{WEDDING_AKAD_TIME}</Text>
            {isInvitation && (
              <>
                <Text
                  fontSize="20px"
                  color="secondaryColorText"
                  py="8px"
                  dangerouslySetInnerHTML={{ __html: WEDDING_AKAD_LOC_NAME }}
                />
                <Text fontSize="16px">{WEDDING_AKAD_LOC_ROAD}</Text>

                {WEDDING_RESEPSI_DRESSCODE && (
                  <Text fontSize="20px" paddingTop="24px">
                    Dresscode: {WEDDING_AKAD_DRESSCODE}
                  </Text>
                )}
                <Text fontSize="16px" dangerouslySetInnerHTML={{ __html: WEDDING_AKAD_NOTE }} />
              </>
            )}
          </Box>
        </Center>
      </WithAnimation>}
      {isInvitation && GOOGLE_MAPS_AKAD && (
        <Center pos="relative" zIndex="99">
          <WithAnimation>
            <ViewLocationButton
              margin="16px auto"
              text={txt.seeLocation[lang]}
              href={GOOGLE_MAPS_AKAD}
              {...BUTTON_PADDING_PROPS}
            />
          </WithAnimation>
        </Center>
      )}
      {isInvitation && (
        <>          
            {shiftTime != 'resepsi' &&<Box
              width="50px"
              height="3px"
              margin="48px auto 8px auto"
              background="bgSecondary"
              borderRadius="8px"
              transform="rotate(-90deg)"
            />}
          <WithAnimation>
            <Center py="24px">
              <Image src={ASSETS_CHEERS} maxW="48px" />
            </Center>
          </WithAnimation>
          <WithAnimation>
            <Center flexDir="column">
              <Heading {...TEXT_PROPS.heading} textTransform="none">
                {txt.reception[lang]}
              </Heading>
              <Box {...TEXT_PROPS.body} textAlign="center">
                <Text fontSize="24px">{WEDDING_RESEPSI_TIME}</Text>
                <Text
                  fontSize="20px"
                  color="secondaryColorText"
                  py="8px"
                  dangerouslySetInnerHTML={{ __html: WEDDING_RESEPSI_LOC_NAME }}
                />
                <Text fontSize="16px">{WEDDING_RESEPSI_LOC_ROAD}</Text>

                {WEDDING_RESEPSI_DRESSCODE && (
                  <Text fontSize="20px" paddingTop="24px">
                    Dresscode: {WEDDING_RESEPSI_DRESSCODE}
                  </Text>
                )}
                <Text fontSize="16px" dangerouslySetInnerHTML={{ __html: WEDDING_RESEPSI_NOTE }} />
              </Box>
            </Center>
          </WithAnimation>
          <Center pos="relative" zIndex="99">
            <WithAnimation>
              <ViewLocationButton
                margin="16px auto 36px auto"
                text={txt.seeLocation[lang]}
                href={GOOGLE_MAPS_RESEPSI}
                {...BUTTON_PADDING_PROPS}
              />
            </WithAnimation>
          </Center>
        </>
      )}

      {isInvitation && ENABLE_QR_INVITATION && false && (
        <div name={ID_QRCODE}>
          <WithAnimation>
            <Box width="100%" height="100%" marginTop="32px">
              <QRCard />
            </Box>
          </WithAnimation>
        </div>
      )}
    </Box>
  );
}

WeddingSection.propTypes = {
  lang: string.isRequired,
};

export default React.memo(WeddingSection);
